import { BasicCustomerFragment, CustomerSettings, useUpdateSettingsMutation } from 'drycut-graphql';
import { Button, CheckBox, Heading, Para, Stack } from 'ui-components';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';
import { CheckCircleIcon } from '@heroicons/react/20/solid';

type Props = {
  customer: BasicCustomerFragment;
};

export function PrivacySettings({ customer }: Props): JSX.Element {
  const { settings } = customer;
  const { value: settingsSaved, setTrue: setSaved } = useBoolean(false);
  const [updateSettings, { loading }] = useUpdateSettingsMutation();

  const formMethods = useForm<CustomerSettings>({
    defaultValues: {
      serviceMessages: settings?.serviceMessages || false,
      loyaltyMessages: settings?.loyaltyMessages || false,
      personalizedOffers: settings?.personalizedOffers || false,
    },
  });

  const onSubmit = async (data: CustomerSettings) => {
    await updateSettings({
      variables: {
        input: {
          code: customer.code,
          data,
        },
      },
    });

    setSaved();
  };

  return (
    <Stack as="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
      <Stack gap={6} className="items-center font-aribau text-center text-pretty">
        <Heading variant="h2">Let's keep in touch!</Heading>

        <Para pretty align="center" className="max-w-prose">
          Hilf uns Deine DRYCUT Experience noch besser zu machen und wähle genau die Nachrichten
          aus, die Du gerne weiterhin von uns per SMS erhalten möchtest. Dann können wir so mit Dir
          kommunizieren wie Du es Dir wünscht!
        </Para>

        <FormProvider {...formMethods}>
          <Stack gap={5} className="max-w-xl">
            <Controller
              name="serviceMessages"
              render={({ field }) => (
                <CheckBox
                  {...field}
                  label={
                    <Para size="sm">
                      Zusätzliche Infos betreffend deiner Buchungen, etwa eine kurze Erinnerung wenn
                      Du an der Reihe bist, sollte die Wartezeit doch einmal etwas länger als 15
                      Minuten sein
                    </Para>
                  }
                />
              )}
            />

            <Controller
              name="loyaltyMessages"
              render={({ field }) => (
                <CheckBox
                  {...field}
                  label={
                    <Para size="sm">
                      Persönliche Gutscheine nach jedem fünften Schnitt, im Rahmen unseres
                      Treueprogramms
                    </Para>
                  }
                />
              )}
            />

            <Controller
              name="personalizedOffers"
              render={({ field }) => (
                <CheckBox
                  {...field}
                  label={
                    <Para size="sm">
                      Individuelle Angebote und Rabatte nur für dich; selbstverständlich nur
                      betreffend DRYCUT
                    </Para>
                  }
                />
              )}
            />
          </Stack>

          {settingsSaved && (
            <Stack direction="row" gap={1}>
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
              <Para className="antialiased text-green-500">Einstellungen gespeichert</Para>
            </Stack>
          )}

          <Stack className="w-80">
            <Button type="submit" loading={loading}>
              Einstellungen Speichern
            </Button>
          </Stack>
        </FormProvider>
      </Stack>
    </Stack>
  );
}
